import apiClient from '../client';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';
import type {
  ClinicalNoteResponse,
  ClinicalNoteListResponse,
  CreateClinicalNoteRequest,
  UpdateClinicalNoteRequest,
  EncounterResponse,
  EncounterListResponse,
  CreateEncounterRequest,
  EncounterStatus,
  NoteStatus,
  NoteType,
} from '@/types/clinical.types';

export interface ClinicalNoteFilterParams extends PaginationParams {
  search?: string;
  status?: NoteStatus | string;
  type?: NoteType | string;
}

export const clinicalNotesApi = {
  getById: async (id: string): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.get<ClinicalNoteResponse>(`/v1/clinical-notes/${id}`);
    return response.data;
  },

  getByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<ClinicalNoteListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<ClinicalNoteListResponse>>(
      `/v1/clinical-notes/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  getPatientTimeline: async (
    patientId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<ClinicalNoteListResponse[]> => {
    const response = await apiClient.get<ClinicalNoteListResponse[]>(
      `/v1/clinical-notes/patient/${patientId}/timeline`,
      { params }
    );
    return response.data;
  },

  getByEncounter: async (encounterId: string): Promise<ClinicalNoteListResponse[]> => {
    const response = await apiClient.get<ClinicalNoteListResponse[]>(
      `/v1/clinical-notes/encounter/${encounterId}`
    );
    return response.data;
  },

  create: async (data: CreateClinicalNoteRequest): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.post<ClinicalNoteResponse>('/v1/clinical-notes', data);
    return response.data;
  },

  update: async (id: string, data: UpdateClinicalNoteRequest): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.put<ClinicalNoteResponse>(`/v1/clinical-notes/${id}`, data);
    return response.data;
  },

  sign: async (id: string): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.post<ClinicalNoteResponse>(`/v1/clinical-notes/${id}/sign`);
    return response.data;
  },

  addAddendum: async (id: string, content: string): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.post<ClinicalNoteResponse>(
      `/v1/clinical-notes/${id}/addendum`,
      { content }
    );
    return response.data;
  },

  getRecent: async (params?: PaginationParams): Promise<PaginatedResponse<ClinicalNoteListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<ClinicalNoteListResponse>>(
      '/v1/clinical-notes/recent',
      { params }
    );
    return response.data;
  },

  getAll: async (params?: ClinicalNoteFilterParams): Promise<PaginatedResponse<ClinicalNoteListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<ClinicalNoteListResponse>>(
      '/v1/clinical-notes',
      { params }
    );
    return response.data;
  },

  getDrafts: async (): Promise<ClinicalNoteListResponse[]> => {
    const response = await apiClient.get<ClinicalNoteListResponse[]>('/v1/clinical-notes/drafts');
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/clinical-notes/${id}`);
  },

  cancel: async (id: string, reason: string): Promise<ClinicalNoteResponse> => {
    const response = await apiClient.post<ClinicalNoteResponse>(
      `/v1/clinical-notes/${id}/cancel`,
      { reason }
    );
    return response.data;
  },
};

export interface EncounterFilterParams extends PaginationParams {
  status?: EncounterStatus;
  type?: string;
  patientId?: string;
  startDate?: string;
  endDate?: string;
}

export const encountersApi = {
  getAll: async (params?: EncounterFilterParams): Promise<PaginatedResponse<EncounterListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<EncounterListResponse>>(
      '/v1/encounters',
      { params }
    );
    return response.data;
  },

  getById: async (id: string): Promise<EncounterResponse> => {
    const response = await apiClient.get<EncounterResponse>(`/v1/encounters/${id}`);
    return response.data;
  },

  getByPatient: async (
    patientId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<EncounterListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<EncounterListResponse>>(
      `/v1/encounters/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  getByProvider: async (
    providerId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<EncounterListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<EncounterListResponse>>(
      `/v1/encounters/provider/${providerId}`,
      { params }
    );
    return response.data;
  },

  getByStatus: async (
    status: EncounterStatus,
    params?: PaginationParams
  ): Promise<PaginatedResponse<EncounterListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<EncounterListResponse>>(
      `/v1/encounters/status/${status}`,
      { params }
    );
    return response.data;
  },

  getToday: async (): Promise<EncounterListResponse[]> => {
    const response = await apiClient.get<EncounterListResponse[]>('/v1/encounters/today');
    return response.data;
  },

  getMyEncounters: async (params?: PaginationParams): Promise<PaginatedResponse<EncounterListResponse>> => {
    const response = await apiClient.get<PaginatedResponse<EncounterListResponse>>(
      '/v1/encounters/my',
      { params }
    );
    return response.data;
  },

  getInProgress: async (): Promise<EncounterListResponse[]> => {
    const response = await apiClient.get<EncounterListResponse[]>('/v1/encounters/in-progress');
    return response.data;
  },

  create: async (data: CreateEncounterRequest): Promise<EncounterResponse> => {
    const response = await apiClient.post<EncounterResponse>('/v1/encounters', data);
    return response.data;
  },

  start: async (id: string): Promise<EncounterResponse> => {
    const response = await apiClient.post<EncounterResponse>(`/v1/encounters/${id}/start`);
    return response.data;
  },

  complete: async (id: string): Promise<EncounterResponse> => {
    const response = await apiClient.post<EncounterResponse>(`/v1/encounters/${id}/complete`);
    return response.data;
  },

  cancel: async (id: string, reason?: string): Promise<EncounterResponse> => {
    const response = await apiClient.post<EncounterResponse>(`/v1/encounters/${id}/cancel`, null, {
      params: { reason },
    });
    return response.data;
  },

  markNoShow: async (id: string): Promise<EncounterResponse> => {
    const response = await apiClient.post<EncounterResponse>(`/v1/encounters/${id}/no-show`);
    return response.data;
  },
};
